import React, { FC, ReactElement, ReactNode } from 'react';

import './RolloutBlock.css';

import { WarningAltOutlined } from '@duxton-web/icons';
import {
  Alert,
  Divider,
  Flex,
  InputNumber,
  Tag,
  theme,
  Typography,
} from '@duxton-web/ui';

import { assignByDomainMap } from './constants';
import RolloutValue from './RolloutValue';
import ConstraintsEditor from '@/common/ConstraintsEditor';
import {
  getProperlyInstrumentedFacetList,
  isImproperInstrumentedFacet,
  useVariableStatsQuery,
} from '@/common/ConstraintsEditor/Constraint';
import { getAllDomains } from '@/common/domains';
import { isProduction } from '@/common/environment';
import Service, {
  Constraint,
  GenericValue,
  RolloutTarget,
  VariableInfo,
} from '@/lib/App';
import { warn } from '@/utils/logging';
import { getVariableStatsStartDate } from '@/utils/rollout';
import { getVariableFacets } from '@/utils/variable';

export interface RolloutBlockProps {
  rolloutSlice: RolloutTarget;
  variable: VariableInfo;
  disabled?: boolean;
  onChange: (rolloutSlice: RolloutTarget) => void;
  assignBy?: string;
  targetIndex: number;
}

export interface RolloutBlockState {
  isDomainlessFeatureFlag?: boolean;
  disabled?: boolean;
}

export function getAssignBy(variable: VariableInfo, assignBy: string): string {
  if (Service.cache.isFeatureFlagOn('domainless')) {
    return assignBy;
  }
  return assignByDomainMap[variable.domain] || 'pax';
}

export function isSliderConstraint(
  variable: VariableInfo,
  assignBy: string,
  constraint: Constraint,
): boolean {
  return (
    constraint.target === getAssignBy(variable, assignBy) &&
    constraint.op === '/'
  );
}

function getFacetLabelFromDomain(variable: VariableInfo): string | null {
  try {
    if (!variable?.domain) {
      warn(null, 'Variable domain not available for percentage facet label');
      return null;
    }

    const domain = getAllDomains().find((d) => d.name === variable.domain);
    if (!domain) {
      warn(
        null,
        `Domain '${variable.domain}' not found for percentage facet label`,
      );
      return null;
    }

    if (!domain?.targetBy) {
      warn(
        null,
        `targetBy field not available for domain '${variable.domain}' - percentage facet label unavailable`,
      );
      return null;
    }

    const facet = Service.cache
      ?.getAllFacets()
      .find((f) => f.name === domain.targetBy);
    if (!facet) {
      warn(
        null,
        `Facet '${domain.targetBy}' not found for domain '${variable.domain}' - percentage facet label unavailable`,
      );
      return null;
    }

    return facet.title;
  } catch (error) {
    warn(
      error,
      `Error getting facet label from domain for variable '${variable?.name}' with domain '${variable?.domain}'`,
    );
    return null;
  }
}

const RolloutBlock: FC<RolloutBlockProps> = ({
  disabled: incomingDisabled,
  assignBy,
  rolloutSlice,
  variable,
  onChange,
}) => {
  const isDomainlessFeatureFlag = Service.cache.isFeatureFlagOn('domainless');
  let disabled = incomingDisabled;

  if (isDomainlessFeatureFlag && !disabled) disabled = assignBy === undefined;

  const variableStats = useVariableStatsQuery(
    [variable.name],
    getVariableStatsStartDate(),
  );
  const properlyInstrumentedFacetList = getProperlyInstrumentedFacetList(
    variableStats,
  ) as string[];

  function onSliderChange(index: number, v: number): void {
    let updateIndex = index;
    // append when the contraint isn't found
    if (index == -1) {
      updateIndex = rolloutSlice?.constraints.length;
    }

    const newConstraint: Constraint = {
      target: getAssignBy(variable, assignBy),
      op: '/',
      value: `${v}`,
    };

    onChange({
      ...rolloutSlice,
      constraints: Object.assign([], rolloutSlice?.constraints, {
        [updateIndex]: newConstraint,
      }),
    });
  }

  function onRolloutValueChange(v: GenericValue): void {
    onChange({
      ...rolloutSlice,
      ...v,
    });
  }

  function findSliderConstraintIndex(): number {
    let index: number = rolloutSlice?.constraints?.findIndex((constraint) =>
      isSliderConstraint(variable, assignBy, constraint),
    );
    return index;
  }

  function onConstraintsUpdate(constraints: Constraint[]): void {
    if (rolloutSlice?.constraints?.length) {
      let sliderIndex: number = findSliderConstraintIndex();
      if (sliderIndex != -1) {
        constraints.push(rolloutSlice?.constraints[sliderIndex]);
      }
    }

    let copy: RolloutTarget = {
      ...rolloutSlice,
      constraints,
    };

    onChange(copy);
  }

  function findSliderConstraintValue(index: number): number {
    if (index == -1 || !rolloutSlice?.constraints) {
      return 1;
    }
    return +rolloutSlice?.constraints[index]?.value;
  }

  function renderWrapperDivider(title: string): ReactNode {
    return (
      <Tag
        color={theme.getDesignToken().grey10}
        style={{
          marginInlineEnd: 0,
        }}
      >
        {title}
      </Tag>
    );
  }

  function renderConstraintsWrapper(): ReactNode {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {renderWrapperDivider('Constraints')}
          <Divider />
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <ConstraintsEditor
            skipFirst
            constraints={
              rolloutSlice?.constraints?.filter(
                (constraint) =>
                  !isSliderConstraint(variable, assignBy, constraint),
              ) || []
            }
            onChange={onConstraintsUpdate}
            disabled={disabled}
            allowedFacets={getVariableFacets(variable)}
            rolloutVariable={variable}
            mandatoryFacets={variable?.metadata?.mandatoryFacets || []}
          />
        </div>
      </div>
    );
  }

  function renderRolloutPercentageWrapper(): ReactNode {
    const sliderConstraintIndex = findSliderConstraintIndex();
    const sliderConstraintValue = findSliderConstraintValue(
      sliderConstraintIndex,
    );

    // Get facet label from domain's targetBy field
    const facetLabel = getFacetLabelFromDomain(variable);
    const percentageLabel = facetLabel
      ? `Percentage of ${facetLabel}`
      : 'Percentage';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {renderWrapperDivider(percentageLabel)}
          <Divider />
        </div>
        <InputNumber
          min={0}
          max={100}
          style={{ width: '100%' }}
          value={sliderConstraintValue * 100}
          formatter={(value) => `${value}%`}
          parser={(value) => value?.replace('%', '') as unknown as number}
          precision={0}
          disabled={disabled}
          onChange={(val) => {
            onSliderChange(sliderConstraintIndex, val / 100);
          }}
        />
      </div>
    );
  }

  function renderValueWrapper(): ReactNode {
    const { value, string, script } = rolloutSlice;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {renderWrapperDivider('Value')}
          <Divider />
        </div>
        <RolloutValue
          value={{ value, string, script }}
          variable={variable}
          onChange={onRolloutValueChange}
          disabled={disabled}
        />
      </div>
    );
  }

  function renderInproperFacetsUsed(): ReactNode {
    if (!rolloutSlice?.constraints?.length) return false;

    let showFacetAlert = false;

    if (rolloutSlice?.constraints?.length && isProduction)
      rolloutSlice?.constraints?.forEach((constraint) => {
        if (
          !isSliderConstraint(variable, assignBy, constraint) &&
          constraint?.target &&
          isImproperInstrumentedFacet(
            properlyInstrumentedFacetList,
            constraint?.target,
          )
        ) {
          showFacetAlert = true;
          return false;
        }
      });

    if (!showFacetAlert) return false;

    return (
      <Alert
        type="warning"
        message="Some constraint facets may not be implemented in code"
        description={
          <Typography.Text>
            There is no data available for the facets of the constraints marked
            in (<WarningAltOutlined style={{ color: '#faad14' }} />
            ). If they are not implemented in code, these facets will have no
            effect.
          </Typography.Text>
        }
      />
    );
  }

  function renderContent(): ReactElement {
    return (
      <Flex
        className="rollout-block"
        vertical
        gap={8}
        style={{
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {renderValueWrapper()}
        {renderConstraintsWrapper()}
        {renderInproperFacetsUsed()}
        {!variable?.metadata?.canaryConfig?.enabled &&
          renderRolloutPercentageWrapper()}
      </Flex>
    );
  }

  return renderContent();
};

export default RolloutBlock;
